import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { Box, Tooltip } from '@mui/material';
import { Warning } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { getAllMemberSwishStatus } from 'src/shared/services/memberSwishStatusService';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { displayStripePeriodicityOnly } from 'src/shared/models/enums/stripePeriodicity.enum';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { MemberSwishStatus } from '../models/memberSwishStatus';
import { MemberSwishStatusSearchFilter } from '../models/memberSwishStatusSearchFilter';

type MemberSwishStatusDataGridProps = {
  searchFilter: MemberSwishStatusSearchFilter;
  searchFilterCallback: (searchFilter: MemberSwishStatusSearchFilter) => void;
};

export const MemberSwishStatusDataGrid = (props: MemberSwishStatusDataGridProps) => {
  const { searchFilter, searchFilterCallback } = props;
  const { t } = useTranslation(['invoice', 'common']);
  const isMounted = useIsMounted();

  const { scrollIntoView, scrollRef } = useScrollIntoView();

  const [rows, setRows] = useState<MemberSwishStatus[]>([]);

  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });

  const updateData = <K extends keyof DataGridModel>(k: K, v: DataGridModel[K]) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
    }
    try {
      const result = await getAllMemberSwishStatus(searchFilter);
      const memberSwishStatusResult = extendObjectWithId(result.data.results, 'swishStatus_id');
      updateData('totalRows', result.data.totalItems);

      setTimeout(() => {
        setRows(memberSwishStatusResult);
      }, 100);
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
      }
    }
  }, [isMounted, searchFilter]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const onRowClick = () => {
    ConsoleHelper.log();
  };

  // Översätt consent-status (CREATED/SIGNING/ACTIVE/...) med fallback till råvärdet.
  const consentStatusLabel = (status: string) => {
    if (!status) return '';
    const key = `invoice:swishStatus.consentStatusValues.${status}`;
    const translated = t(key);
    return translated === key ? status : translated;
  };

  // Medgivandet måste vara aktivt för att dragning ska ske — annars varnar vi (speglar autogiros mandat-varning).
  const isConsentInactive = (row: MemberSwishStatus) => row.consentStatus !== 'ACTIVE';

  const columns: GridColDef[] = [
    {
      field: 'memberNr',
      headerName: t('invoice:swishStatus.tableHeader.memberNr'),
      sortable: true,
      flex: 0.7,
      renderCell: (params: GridCellParams) => {
        return <Link to={`${RoutesEnum.MemberDetails}/${params.row.customer_id}`}>{params.row.memberNr}</Link>;
      },
    },
    {
      field: 'firstName',
      headerName: t('invoice:swishStatus.tableHeader.firstName'),
      sortable: true,
      flex: 0.8,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.firstName}</>;
      },
    },
    {
      field: 'lastName',
      headerName: t('invoice:swishStatus.tableHeader.lastName'),
      sortable: true,
      flex: 1.1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.lastName}</>;
      },
    },
    {
      field: 'periodicity',
      headerName: t('invoice:swishStatus.tableHeader.periodicity'),
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      type: 'number',
      flex: 0.8,
      renderCell: (params: GridCellParams) => {
        return <>{displayStripePeriodicityOnly(params.row, t)}</>;
      },
    },
    {
      field: 'created',
      headerName: t('invoice:swishStatus.tableHeader.createdAt'),
      sortable: true,
      flex: 0.8,
      renderCell: (params: GridCellParams) => {
        return <>{DateHelper.getDateTime(params.row.created)}</>;
      },
    },
    {
      field: 'nextInvoiceAfter',
      headerName: t('invoice:swishStatus.tableHeader.nextInvoiceAfter'),
      sortable: true,
      flex: 0.8,
      renderCell: (params: GridCellParams) => {
        const inactive = isConsentInactive(params.row);
        return (
          <Box display='flex' alignItems='center' gap={0.5}>
            <span style={inactive ? { textDecoration: 'line-through' } : undefined}>{DateHelper.getDate(params.row.nextInvoiceAfter)}</span>
            {inactive && (
              <Tooltip
                title={`${t('invoice:swishStatus.consentNotActiveWarning')} (${consentStatusLabel(params.row.consentStatus)})`}
                arrow
                placement='left'
              >
                <Warning color='error' sx={{ fontSize: 20 }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'nextInvoiceAmount',
      headerAlign: 'left',
      align: 'left',
      headerName: t('invoice:swishStatus.tableHeader.amount'),
      sortable: true,
      type: 'number',
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        // -1 = beloppet ännu inte fastställt (samma konvention som autogiro) — visa text i stället för "-1 SEK".
        if (params.row.nextInvoiceAmount === -1) return <>{t('invoice:autogiro.amountNotDetermined')}</>;
        return <AmountWithCurrency amount={params.row.nextInvoiceAmount} />;
      },
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiPaper-root': {
          boxShadow: 'none',
        },
      }}
      ref={scrollRef}
    >
      <DataGrid<MemberSwishStatus>
        autoHeight
        columns={columns}
        rows={rows}
        disablePointer
        disableSelectionOnClick
        onRowClick={onRowClick}
        dataModel={data}
        checkboxSelection={false}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        selectionModel={[]}
        sortModel={[
          {
            field: searchFilter.sortCol,
            sort: searchFilter.sortDir,
          },
        ]}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length > 0) {
            const { field } = newSortModel[0];
            searchFilterCallback({
              ...searchFilter,
              pageIndex: 0,
              sortCol: field,
              sortDir: newSortModel[0].sort,
            });
          } else {
            searchFilterCallback({
              ...searchFilter,
              pageIndex: 0,
              sortCol: searchFilter.sortCol,
              sortDir: searchFilter.sortDir === 'asc' ? 'desc' : 'asc',
            });
          }
        }}
        onSelectionModelChange={() => ConsoleHelper.log('')}
      />
    </Box>
  );
};
